import config from '@/config';
import { defineStore } from 'pinia';

// Função para carregar configurações do localStorage
const loadFromStorage = (key: string, defaultValue: any) => {
    try {
        const stored = localStorage.getItem(key);

        // Se não existe ou é null, retornar defaultValue
        if (!stored || stored === null) {
            return defaultValue;
        }

        // Verificar se é uma string inválida como "undefined" ou "null"
        const trimmed = stored.trim();
        if (trimmed === 'undefined' || trimmed === 'null' || trimmed === '') {
            // Limpar valor inválido do localStorage
            localStorage.removeItem(key);
            return defaultValue;
        }

        // Tentar fazer parse do JSON
        const parsed = JSON.parse(stored);
        return parsed;
    } catch (error) {
        // Se houver erro no parse, limpar o valor inválido e retornar defaultValue
        console.warn(`Erro ao carregar ${key} do localStorage:`, error);
        localStorage.removeItem(key);
        return defaultValue;
    }
};

// Função para salvar configurações no localStorage
const saveToStorage = (key: string, value: any) => {
    try {
        // Não salvar valores undefined ou null
        if (value === undefined || value === null) {
            // Se o valor for undefined/null, remover a chave do localStorage
            localStorage.removeItem(key);
            return;
        }

        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.warn(`Erro ao salvar ${key} no localStorage:`, error);
        // Em caso de erro, tentar remover a chave para evitar dados corrompidos
        try {
            localStorage.removeItem(key);
        } catch (removeError) {
            // Ignorar erro ao remover
        }
    }
};

export const useCustomizerStore = defineStore('customizer', {
    state: () => ({
        Sidebar_drawer: loadFromStorage('sidebar_drawer', config.Sidebar_drawer),
        Customizer_drawer: false, // Sempre fechado ao carregar a página
        mini_sidebar: loadFromStorage('mini_sidebar', config.mini_sidebar),
        setHorizontalLayout: loadFromStorage('horizontal_layout', config.setHorizontalLayout), // Horizontal layout
        setRTLLayout: loadFromStorage('rtl_layout', config.setRTLLayout), // RTL layout
        actTheme: loadFromStorage('theme', config.actTheme),
        boxed: loadFromStorage('boxed', config.boxed),
        setBorderCard: loadFromStorage('border_card', config.setBorderCard),
        darkMode: loadFromStorage('dark_mode', false), // Modo escuro
        language: loadFromStorage('language', 'pt-BR') // Idioma
    }),

    getters: {
        // Getter para verificar se está em modo escuro
        isDarkMode: (state) => state.darkMode,
        // Getter para obter o idioma atual
        currentLanguage: (state) => state.language
    },
    actions: {
        SET_SIDEBAR_DRAWER() {
            this.Sidebar_drawer = !this.Sidebar_drawer;
            saveToStorage('sidebar_drawer', this.Sidebar_drawer);
        },
        SET_MINI_SIDEBAR(payload: any) {
            this.mini_sidebar = payload;
            saveToStorage('mini_sidebar', payload);
        },
        SET_CUSTOMIZER_DRAWER(payload: any) {
            this.Customizer_drawer = payload;
            saveToStorage('customizer_drawer', payload);
        },
        SET_LAYOUT(payload: any) {
            this.setHorizontalLayout = payload;
            saveToStorage('horizontal_layout', payload);
        },
        SET_THEME(payload: any) {
            this.actTheme = payload;
            saveToStorage('theme', payload);
        },
        SET_CARD_BORDER(payload: any) {
            this.setBorderCard = payload;
            saveToStorage('border_card', payload);
        },
        SET_RTL_LAYOUT(payload: any) {
            this.setRTLLayout = payload;
            saveToStorage('rtl_layout', payload);
        },
        SET_BOXED(payload: any) {
            this.boxed = payload;
            saveToStorage('boxed', payload);
        },
        // Novas actions para modo escuro e idioma
        SET_DARK_MODE(payload: boolean) {
            this.darkMode = payload;
            saveToStorage('dark_mode', payload);
        },
        SET_LANGUAGE(payload: string) {
            this.language = payload;
            saveToStorage('language', payload);
        },
        // Função para resetar todas as configurações
        RESET_SETTINGS() {
            this.Sidebar_drawer = config.Sidebar_drawer;
            this.Customizer_drawer = config.Customizer_drawer;
            this.mini_sidebar = config.mini_sidebar;
            this.setHorizontalLayout = config.setHorizontalLayout;
            this.setRTLLayout = config.setRTLLayout;
            this.actTheme = config.actTheme;
            this.boxed = config.boxed;
            this.setBorderCard = config.setBorderCard;
            this.darkMode = false;
            this.language = 'pt-BR';

            // Limpar localStorage
            localStorage.removeItem('sidebar_drawer');
            localStorage.removeItem('customizer_drawer');
            localStorage.removeItem('mini_sidebar');
            localStorage.removeItem('horizontal_layout');
            localStorage.removeItem('rtl_layout');
            localStorage.removeItem('theme');
            localStorage.removeItem('boxed');
            localStorage.removeItem('border_card');
            localStorage.removeItem('dark_mode');
            localStorage.removeItem('language');
        }
    }
});
